import { Card, CardHeader } from '../../../components/Card.jsx'
import { LineChart, RankTable, MethodNote, fmtNum } from '../ui.jsx'

/**
 * National Trends & Forecasts — activity over the last 12 months so
 * platform admins can see whether encounters, diagnoses and new patient
 * registrations are rising or falling. Forecast overlays will layer onto
 * this same series once a baseline of history accrues.
 */
export default function TrendsForecasts({ data }) {
  const tr = data.trends || {}
  const months = tr.months || []

  // Simple month-over-month deltas for the most recent point.
  const last = months[months.length - 1]
  const prev = months[months.length - 2]
  const delta = (key) => {
    if (!last || !prev) return null
    const a = last[key] || 0
    const b = prev[key] || 0
    if (b === 0) return null
    return Math.round(((a - b) / b) * 100)
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader
          title="Trends & forecasts"
          subtitle={`Platform activity over the last ${months.length} months. Encounters, coded diagnoses and new patient registrations.`}
          right={
            <span className="text-[10px] uppercase tracking-wide border rounded-full px-2 py-0.5 bg-wantok-50 text-wantok-700 border-wantok-200">
              12-month trend
            </span>
          }
        />
        <MethodNote>
          Monthly counts over the last 12 months by record date: encounters,
          confirmed coded diagnoses, and new patient registrations.
        </MethodNote>
        <LineChart
          data={months}
          series={[
            { key: 'encounters', label: 'Encounters', colour: 'wantok' },
            { key: 'diagnoses', label: 'Diagnoses', colour: 'coral' },
            { key: 'new_patients', label: 'New patients', colour: 'sky' },
          ]}
        />
      </Card>

      <Card>
        <CardHeader
          title="Monthly figures"
          subtitle="Latest month vs prior shown as month-over-month change in the header tiles below the table."
        />
        <div className="flex flex-wrap gap-3 mb-3 text-xs">
          {[
            { key: 'encounters', label: 'Encounters' },
            { key: 'diagnoses', label: 'Diagnoses' },
            { key: 'new_patients', label: 'New patients' },
          ].map((m) => {
            const d = delta(m.key)
            const up = d != null && d >= 0
            return (
              <span
                key={m.key}
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 ${
                  d == null
                    ? 'bg-slate-50 text-slate-500 border-slate-200'
                    : up
                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                      : 'bg-rose-50 text-rose-700 border-rose-200'
                }`}
              >
                {m.label}: {d == null ? '—' : `${up ? '▲' : '▼'} ${Math.abs(d)}% MoM`}
              </span>
            )
          })}
        </div>
        <RankTable
          rows={[...months].reverse()}
          columns={[
            { key: 'month', label: 'Month', cellClass: 'font-medium text-slate-800' },
            { key: 'encounters', label: 'Encounters', align: 'right', render: (r) => fmtNum(r.encounters) },
            { key: 'diagnoses', label: 'Diagnoses', align: 'right', render: (r) => fmtNum(r.diagnoses) },
            { key: 'new_patients', label: 'New patients', align: 'right', render: (r) => fmtNum(r.new_patients) },
          ]}
        />
      </Card>
    </div>
  )
}
